import { Box, Flex } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { Button } from "ui/Button/Button";
import { BagIcon } from "ui/Icons/Icons";
import { TitleM } from "ui/Typography/Typography";

import { calculatePriceExceedance, isOverPriceLimit } from "../utils/helpers";

type EndPreparationButtonProps = {
  bagOfPrice: number;
  totalPrice: number;
  maximumThreshold: number;
  minimumDistinctSkus: number | null;
  distinctSkusCount: number;
  onClickButton: () => void;
};

export function EndPreparationButton({
  bagOfPrice,
  totalPrice,
  onClickButton,
  maximumThreshold,
  minimumDistinctSkus,
  distinctSkusCount,
}: EndPreparationButtonProps) {
  const missingDistinctSkus =
    minimumDistinctSkus !== null ? Math.max(0, minimumDistinctSkus - distinctSkusCount) : 0;
  const priceGap = totalPrice < bagOfPrice ? Math.round((bagOfPrice - totalPrice) * 100) / 100 : 0;
  const overMaximum = isOverPriceLimit(bagOfPrice, totalPrice, maximumThreshold);
  const isDisabled = missingDistinctSkus > 0 || priceGap > 0 || overMaximum;

  if (isDisabled) {
    const { exceedAmount } = calculatePriceExceedance(
      overMaximum,
      bagOfPrice,
      totalPrice,
      maximumThreshold,
    );
    return (
      <Box w="100%" h="72px" px="s100" py="s150" pos="fixed" bottom={0} zIndex={1}>
        <Button isFullWidth variant="primary" size="lg" position="relative" disabled>
          <BagIcon boxSize="24px" position="absolute" left="16px" />
          {missingDistinctSkus > 0 ? (
            <FormattedMessage
              id="pages.tgtg-bag-selection-page.end-button.min-distinct-skus"
              values={{ count: missingDistinctSkus }}
            />
          ) : (
            <FormattedMessage
              id={
                overMaximum
                  ? "pages.tgtg-bag-selection-page.end-button.over-maximum"
                  : "pages.tgtg-bag-selection-page.end-button.progress"
              }
              values={{ price: overMaximum ? exceedAmount : priceGap }}
            />
          )}
        </Button>
      </Box>
    );
  }

  return (
    <Box w="100%" h="72px" px="s100" py="s150" pos="fixed" bottom={0} zIndex={1}>
      <Button
        isFullWidth
        variant="primary"
        size="lg"
        onClick={onClickButton}
        display="flex"
        justifyContent="space-between"
        px="s200"
      >
        <Flex align="center" gap="s200">
          <BagIcon boxSize="24px" />
          <FormattedMessage id="pages.tgtg-bag-selection-page.end-button.complete-preparation" />
        </Flex>
        <TitleM color="white">{totalPrice} €</TitleM>
      </Button>
    </Box>
  );
}
